/**
 * Mask results shared between the placeholders of image patterns (origin and replicas), which otherwise compute the
 *  same mask once each: N canvases and N getImageData readbacks per load. Everything else keeps its own mask.
 */

interface PatternMember {
    _imagePatternMember?: boolean;
}

// A few masks and sizes per template; an evicted entry is only rebuilt, placeholders keep the canvas they hold
const MAX_ENTRIES = 8;

const fullMaskBySrc = new Map<string, boolean>();
// Weakly: the placeholders holding a canvas keep it alive, the cache only lets them share it (canvases can be ~60 MB)
const maskCanvasByInputs = new Map<string, WeakRef<HTMLCanvasElement>>();

const remember = <V>(cache: Map<string, V>, key: string, value: V) => {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > MAX_ENTRIES) cache.delete(cache.keys().next().value as string);
    return value;
};

const recall = <V>(cache: Map<string, V>, key: string) => {
    const value = cache.get(key);
    if (value !== undefined) remember(cache, key, value);
    return value;
};

export const markImagePatternMember = (placeholder: object) => {
    (placeholder as PatternMember)._imagePatternMember = true;
};

export const isImagePatternMember = (placeholder: object | null | undefined) =>
    !!(placeholder as PatternMember | null | undefined)?._imagePatternMember;

/** Same answer for every mask loaded from the same src; elements without one are checked as before */
export const isFullMaskBySrc = (mask: HTMLImageElement, check: (mask: HTMLImageElement) => boolean) => {
    const src = mask.src;
    if (!src) return check(mask);
    const known = recall(fullMaskBySrc, src);
    return known ?? remember(fullMaskBySrc, src, check(mask));
};

/**
 * The processed mask canvas for these inputs, built once. Callers only draw from it: a different size is another
 *  entry, never a resize of the shared canvas. Null when the mask has no src to tell it apart.
 */
export const sharedMaskCanvas = (
    mask: HTMLImageElement,
    color: string,
    width: number,
    height: number,
    build: () => HTMLCanvasElement
) => {
    const src = mask.src;
    if (!src) return null;
    const key = `${color}|${width}|${height}|${src}`;
    const known = recall(maskCanvasByInputs, key)?.deref();
    if (known) return known;
    const built = build();
    remember(maskCanvasByInputs, key, new WeakRef(built));
    return built;
};

/** For tests */
export const clearImagePatternMasks = () => {
    fullMaskBySrc.clear();
    maskCanvasByInputs.clear();
};
